import { useState, Suspense } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Textarea,
  SelectField,
  FormHelperText,
  Select,
  Divider,
  CircularProgress,
} from "@chakra-ui/react";

// Language Management
import { useTranslation } from "react-i18next";

// Import axios
import axios from "axios";

// Service
import rdvService from "services/rdvService";
import Utils from "services/Utils";

const CCompleteRequest = ({ isOpen, onClose, accessToken, originalRdvId }) => {
  // Init translation
  const { t } = useTranslation();
  const timeZoneInfo = Utils.getLocalTimeZoneInfo().timeZone;

  // State for form fields
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [subject, setSubject] = useState("");
  const [issue, setIssue] = useState("");
  const [closingRemarks, setClosingRemarks] = useState("");

  //   In Progress
  const [saving, setSaving] = useState(false);

  // Function to handle form submission
  const handleSubmit = async () => {
    // Set saving to true
    setSaving(true);
    // Update the RDV
    const payload = {
      start_date: startDate,
      end_date: endDate,
      subject,
      issue,
      closing_remarks: closingRemarks,
      is_closed: true,
      status: "Completed",
      timezone: timeZoneInfo,
    };
    await rdvService.update({
      pk: originalRdvId, // Primary key of the RDV
      obj: payload,
      accessToken,
    });
    // set saving to false
    setSaving(false);
    // Close the dialog
    onClose();
  };

  // Function to update RDV
  const updateRdv = async ({ payload }) => {
    // Update the RDV
    await rdvService.update({
      pk: originalRdvId, // Primary key of the RDV
      obj: payload,
      accessToken,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("admin__appointments__complete_req")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Subject */}
          <FormControl>
            <FormLabel>{t("admin__appointments_table__subject")}</FormLabel>
            <Select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            >
              <option value={t("family_topic__decision_support")}>
                {t("family_topic__decision_support")}
              </option>
              <option
                value={t("family_topic__custody_arrangement_coparenting")}
              >
                {t("family_topic__custody_arrangement_coparenting")}
              </option>
              <option
                value={t("family_topic__education_maintenance_contribution")}
              >
                {t("family_topic__education_maintenance_contribution")}
              </option>
              <option value={t("family_topic__other_miscellaneous_situations")}>
                {t("family_topic__other_miscellaneous_situations")}
              </option>
              <option value={t("family_topic__same_sex_family")}>
                {t("family_topic__same_sex_family")}
              </option>
              <option value={t("family_topic__blended_family")}>
                {t("family_topic__blended_family")}
              </option>
              <option value={t("family_topic__siblings")}>
                {t("family_topic__siblings")}
              </option>
              <option
                value={t("family_topic__grandparents_children_grandchildren")}
              >
                {t("family_topic__grandparents_children_grandchildren")}
              </option>
              <option value={t("family_topic__parents_teenagers")}>
                {t("family_topic__parents_teenagers")}
              </option>
              <option value={t("family_topic__parents_young_adults")}>
                {t("family_topic__parents_young_adults")}
              </option>
              <option value={t("family_topic__separation_divorce")}>
                {t("family_topic__separation_divorce")}
              </option>
            </Select>
          </FormControl>

          {/* Start Date */}
          <FormControl mt={4}>
            <FormLabel>{t("select_rdv_start_date")}</FormLabel>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <FormHelperText>
              {startDate && (
                <Text color="green.500">
                  {t("date_selected")}: {startDate}
                </Text>
              )}
            </FormHelperText>
          </FormControl>

          {/* End Date */}
          <FormControl mt={4}>
            <FormLabel>{t("select_rdv_end_date")}</FormLabel>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <FormHelperText>
              {startDate && (
                <Text color="green.500">
                  {t("date_selected")}: {endDate}
                </Text>
              )}
            </FormHelperText>
          </FormControl>

          {/* Issue */}
          <FormControl mt={4}>
            <FormLabel>{t("select_issue")}</FormLabel>
            <Select value={issue} onChange={(e) => setIssue(e.target.value)}>
              <option value="written_agreement">
                {t("written_agreement")}
              </option>
              <option value="oral_agreement">{t("oral_agreement")}</option>
              <option value="partial_written_agreement">
                {t("partial_written_agreement")}
              </option>
              <option value="significant_progress_conflict_resolution">
                {t("significant_progress_conflict_resolution")}
              </option>
              <option value="interruption_initiated_by_users">
                {t("interruption_initiated_by_users")}
              </option>
              <option value="interruption_initiated_by_mediator">
                {t("interruption_initiated_by_mediator")}
              </option>
              <option value="no_agreement">{t("no_agreement")}</option>
            </Select>
          </FormControl>

          {/* Closing Remarks */}
          <FormControl mt={4}>
            <FormLabel>{t("closing_remarks")}</FormLabel>
            <Textarea
              value={closingRemarks}
              type="text"
              onChange={(e) => setClosingRemarks(e.target.value)}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
            {saving ? (
              <CircularProgress />
            ) : (
              t("admin__appointments__complete_req")
            )}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("cancel")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// export default CreateFirstSessionDialog;
// i18n translations might still be loaded by the http backend
// use react's Suspense
export default function CompleteRequest({
  isOpen,
  onClose,
  accessToken,
  originalRdvId,
}) {
  return (
    <Suspense fallback="loading">
      <CCompleteRequest
        isOpen={isOpen}
        onClose={onClose}
        accessToken={accessToken}
        originalRdvId={originalRdvId}
      />
    </Suspense>
  );
}
